import { CalendarOutlined } from '@ant-design/icons'
import {
  Add,
  Button,
  Edit,
  FormCard,
  FormItem,
  Input,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import {
  Form as AntdForm,
  Collapse,
  DatePicker,
  Dropdown,
  InputNumber,
  Modal,
  TimePicker
} from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useGetInvoicingRateHistory } from '../../hooks/useInvoicingRates'
import { useShiftTypesList } from '../../hooks/useShiftTypesList'
import { useVisitTypesList } from '../../hooks/useVisitTypesList'
import { OverrideLevel } from '../../types/invoicingRates'
import { getRequiredRule } from '../../utils'
import { DATE_FORMAT, TIME_FORMAT } from '../../utils.datetime'

export type InvoicingRateEntry = {
  level: OverrideLevel
  eventTypeId: string | null
  rate: number
  isDeleted?: boolean
}

export type InvoicingRateCardForm = {
  _id?: string
  initialId?: string
  name: string
  baseRate: number
  effectiveDate: dayjs.Dayjs | null
  effectiveTime: dayjs.Dayjs | null
  overrides: InvoicingRateEntry[]
  isDeleted?: boolean
  isNewVersion?: boolean // UI-only: marks a card created via "Schedule rate change"
}

export type InvoicingRateCardProps = {
  fieldName: number
  form: ReturnType<typeof AntdForm.useForm>[0]
  onDirty: () => void
  isLocked: boolean
  requireEffectiveDate: boolean
  onScheduleFutureRate?: () => void
  deleteWarningText?: string
  originalCard?: InvoicingRateCardForm
}

const InvoicingRateCard = ({
  fieldName,
  form,
  onDirty,
  isLocked,
  requireEffectiveDate,
  onScheduleFutureRate,
  deleteWarningText,
  originalCard
}: InvoicingRateCardProps) => {
  const { t } = useTranslation()
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()
  const [historyOpen, setHistoryOpen] = useState(false)

  const cardId = form.getFieldValue(['invoicingRates', fieldName, '_id'])
  const [isEditing, setIsEditing] = useState(!cardId)
  const initialId = AntdForm.useWatch(
    ['invoicingRates', fieldName, 'initialId'],
    form
  )

  const hasPreviousVersion = !!(initialId && initialId !== cardId)

  const { data: rateHistory, isLoading: isHistoryLoading } =
    useGetInvoicingRateHistory(
      initialId ?? cardId ?? '',
      historyOpen && !!(initialId ?? cardId)
    )

  const shiftTypeIds = useMemo(
    () => new Set((shiftTypes || []).filter((st) => st.id).map((st) => st.id!)),
    [shiftTypes]
  )

  const allEventTypes = useMemo(
    () => [
      ...(shiftTypes || [])
        .filter((st) => st.id && !st.isDeleted)
        .map((st) => ({ id: st.id!, name: st.name })),
      ...(visitTypes || [])
        .filter((vt) => vt.id && !vt.isDeleted)
        .map((vt) => ({ id: vt.id!, name: vt.name }))
    ],
    [shiftTypes, visitTypes]
  )

  const eventTypeNameMap = useMemo<Record<string, string>>(
    () => Object.fromEntries(allEventTypes.map((et) => [et.id, et.name])),
    [allEventTypes]
  )

  const name = AntdForm.useWatch(['invoicingRates', fieldName, 'name'], form)
  const cardTitle = name || `Invoicing Rate ${fieldName + 1}`

  const currentOverrides =
    form.getFieldValue(['invoicingRates', fieldName, 'overrides']) || []

  const filterDeletedOverrides = (overrideIndex: number) =>
    form.getFieldValue([
      'invoicingRates',
      fieldName,
      'overrides',
      overrideIndex,
      'isDeleted'
    ]) !== true

  const usedEventTypeIds = currentOverrides
    .filter((_: any, i: number) => !_?.isDeleted)
    .map((o: any) => o?.eventTypeId)
    .filter(Boolean)

  const availableShiftOptions = (shiftTypes || [])
    .filter((st) => st.id && !st.isDeleted && !usedEventTypeIds.includes(st.id))
    .map((st) => ({ key: st.id!, label: st.name }))

  const availableVisitOptions = (visitTypes || [])
    .filter((vt) => vt.id && !vt.isDeleted && !usedEventTypeIds.includes(vt.id))
    .map((vt) => ({ key: vt.id!, label: vt.name }))

  const hasAvailableOptions =
    availableShiftOptions.length > 0 || availableVisitOptions.length > 0

  const groupedMenuItems = [
    ...(availableShiftOptions.length > 0
      ? [
          {
            type: 'group' as const,
            label: 'Shift Types',
            children: availableShiftOptions
          }
        ]
      : []),
    ...(availableVisitOptions.length > 0
      ? [
          {
            type: 'group' as const,
            label: 'Visit Types',
            children: availableVisitOptions
          }
        ]
      : [])
  ]

  const historyColumns = [
    {
      title: 'Effective Date',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      render: (v: string | null) =>
        v ? dayjs(v).format(`${DATE_FORMAT} ${TIME_FORMAT}`) : '—'
    },
    {
      title: 'Base Rate',
      dataIndex: 'baseRate',
      key: 'baseRate',
      render: (v: string) => `£${v}/hr`
    },
    {
      title: 'Changed At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => dayjs(v).format(`${DATE_FORMAT} ${TIME_FORMAT}`)
    }
  ]

  const confirmDelete = () => {
    const doDelete = () => {
      onDirty()
      form.setFieldValue(['invoicingRates', fieldName, 'isDeleted'], true)
    }
    if (deleteWarningText) {
      Modal.confirm({
        title: 'Delete rate card?',
        content: deleteWarningText,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: doDelete
      })
    } else {
      doDelete()
    }
  }

  const handleCancel = () => {
    if (originalCard) {
      const allCards = form.getFieldsValue(true).invoicingRates
      allCards[fieldName] = { ...originalCard }
      form.setFieldsValue({ invoicingRates: allCards })
    }
    setIsEditing(false)
  }

  const handleDeleteOverride = (overrideIndex: number) => {
    onDirty()
    form.setFieldValue(
      ['invoicingRates', fieldName, 'overrides', overrideIndex, 'isDeleted'],
      true
    )
  }

  // View mode
  if (!isEditing) {
    const effectiveDate = form.getFieldValue([
      'invoicingRates',
      fieldName,
      'effectiveDate'
    ])
    const effectiveTime = form.getFieldValue([
      'invoicingRates',
      fieldName,
      'effectiveTime'
    ])
    const baseRate = form.getFieldValue([
      'invoicingRates',
      fieldName,
      'baseRate'
    ])

    const effectiveFromDisplay =
      effectiveDate && effectiveTime
        ? `${dayjs(effectiveDate).format(DATE_FORMAT)}  ${dayjs(effectiveTime).format(TIME_FORMAT)}`
        : effectiveDate
          ? dayjs(effectiveDate).format(DATE_FORMAT)
          : '-'

    const visibleOverrides = currentOverrides.filter((o: any) => !o?.isDeleted)

    return (
      <ViewFormCard
        level={2}
        title={cardTitle}
        rightChildren={
          !isLocked ? (
            <EditActions>
              {onScheduleFutureRate && (
                <Button
                  variant="text"
                  type="button"
                  icon={<CalendarOutlined />}
                  onClick={onScheduleFutureRate}
                >
                  Schedule rate change
                </Button>
              )}
              <Button
                variant="text"
                type="button"
                icon={<Edit />}
                onClick={() => setIsEditing(true)}
              >
                {t('EDIT_RATE')}
              </Button>
            </EditActions>
          ) : undefined
        }
      >
        <ViewSection>
          <ViewLabel>Effective from</ViewLabel>
          <ViewValue>{effectiveFromDisplay}</ViewValue>
        </ViewSection>

        <RatesRow>
          <ViewSection>
            <ViewLabel>Base rate</ViewLabel>
            <ViewValue>
              {baseRate != null ? `${baseRate} GBP/hour` : '-'}
            </ViewValue>
          </ViewSection>

          {visibleOverrides.map((override: any) => (
            <ViewSection key={override.eventTypeId}>
              <ViewLabel>
                {eventTypeNameMap[override.eventTypeId] || override.eventTypeId}
              </ViewLabel>
              <ViewValue>
                {override.rate != null ? `${override.rate} GBP/hour` : '-'}
              </ViewValue>
            </ViewSection>
          ))}
        </RatesRow>

        {hasPreviousVersion && (
          <>
            <ViewDivider />
            <Collapse
              ghost
              onChange={(keys) => setHistoryOpen(keys.length > 0)}
              items={[
                {
                  key: 'history',
                  label: <Text>View rate history</Text>,
                  children: isHistoryLoading ? (
                    <Text>Loading history...</Text>
                  ) : (
                    <HistoryTable>
                      <thead>
                        <tr>
                          {historyColumns.map((col) => (
                            <th key={col.key}>{col.title}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(rateHistory ?? []).map((entry) => (
                          <tr key={entry._id}>
                            {historyColumns.map((col) => (
                              <td key={col.key}>
                                {col.render
                                  ? col.render((entry as any)[col.dataIndex])
                                  : (entry as any)[col.dataIndex]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </HistoryTable>
                  )
                }
              ]}
            />
          </>
        )}
      </ViewFormCard>
    )
  }

  // Edit mode
  return (
    <ViewFormCard
      level={2}
      title={cardTitle}
      rightChildren={
        <EditActions>
          {cardId && originalCard && (
            <Button variant="text" type="button" onClick={handleCancel}>
              {t('CANCEL')}
            </Button>
          )}
          <Button
            variant="text"
            type="button"
            icon={<Trash color={theme.color.neutral.color4} />}
            onClick={confirmDelete}
          >
            <DeleteText>{t('DELETE')}</DeleteText>
          </Button>
        </EditActions>
      }
    >
      <AntdForm.Item name={[fieldName, '_id']} noStyle>
        <input type="hidden" />
      </AntdForm.Item>
      <AntdForm.Item name={[fieldName, 'initialId']} noStyle>
        <input type="hidden" />
      </AntdForm.Item>

      <FormItem
        name={[fieldName, 'name']}
        label={<Text>Rate card name</Text>}
        rules={[getRequiredRule('Rate card name')]}
      >
        <Input />
      </FormItem>

      {requireEffectiveDate && (
        <>
          <FormItem
            name={[fieldName, 'effectiveDate']}
            label={<Text>Effective from</Text>}
            rules={[getRequiredRule('Effective from')]}
          >
            <DatePicker format={DATE_FORMAT} />
          </FormItem>

          <FormItem
            name={[fieldName, 'effectiveTime']}
            label={<Text>Effective time</Text>}
            rules={[getRequiredRule('Effective time')]}
          >
            <TimePicker format={TIME_FORMAT} needConfirm={false} />
          </FormItem>
        </>
      )}

      <BaseRateRow>
        <FormItem
          name={[fieldName, 'baseRate']}
          label={<Text>Base rate</Text>}
          rules={[getRequiredRule('Base rate')]}
        >
          <InputNumber min={0} addonAfter="GBP / hour" style={{ width: 200 }} />
        </FormItem>

        <AntdForm.List name={[fieldName, 'overrides']}>
          {(fields, { add: addOverride }) => {
            const overrideFields = fields.filter((f) =>
              filterDeletedOverrides(f.name)
            )

            return (
              <>
                <Dropdown
                  menu={{
                    items: groupedMenuItems,
                    onClick: ({ key }) => {
                      addOverride({
                        level: shiftTypeIds.has(key)
                          ? OverrideLevel.SHIFT_TYPE
                          : OverrideLevel.VISIT_TYPE,
                        eventTypeId: key,
                        rate: undefined
                      })
                    }
                  }}
                  disabled={!hasAvailableOptions}
                >
                  <Button type="button" variant="text" icon={<Add />}>
                    Add override
                  </Button>
                </Dropdown>

                {overrideFields.length > 0 && (
                  <OverridesSection>
                    <OverridesDivider />
                    <OverridesHeading>Overrides</OverridesHeading>
                    {overrideFields.map((overrideField) => {
                      const eventTypeId = form.getFieldValue([
                        'invoicingRates',
                        fieldName,
                        'overrides',
                        overrideField.name,
                        'eventTypeId'
                      ])
                      const eventTypeName =
                        eventTypeNameMap[eventTypeId] || eventTypeId

                      return (
                        <OverrideRow key={overrideField.key}>
                          <FormItem
                            name={[overrideField.name, 'rate']}
                            label={<Text>{eventTypeName}</Text>}
                            rules={[getRequiredRule(eventTypeName)]}
                          >
                            <InputNumber
                              min={0}
                              addonAfter="GBP / hour"
                              style={{ width: 200 }}
                            />
                          </FormItem>
                          <Button
                            variant="text"
                            type="button"
                            icon={<Trash color={theme.color.neutral.color4} />}
                            onClick={() =>
                              handleDeleteOverride(overrideField.name)
                            }
                          >
                            <DeleteText>{t('REMOVE')}</DeleteText>
                          </Button>
                        </OverrideRow>
                      )
                    })}
                  </OverridesSection>
                )}
              </>
            )
          }}
        </AntdForm.List>
      </BaseRateRow>
    </ViewFormCard>
  )
}

const ViewFormCard = styled(FormCard)`
  > div:last-child {
    gap: 16px;
  }
`

const EditActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ViewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ViewLabel = styled(Text)`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const ViewValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const RatesRow = styled.div`
  display: flex;
  gap: 48px;
  flex-wrap: wrap;
`

const ViewDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.neutral.color8};
  width: 100%;
`

const DeleteText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-weight: 500;
`

const BaseRateRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  > *:first-child {
    flex: none;
  }
`

const OverridesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const OverridesDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.neutral.color8};
  width: 100%;
`

const OverridesHeading = styled(Text)`
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const OverrideRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .ant-form-item {
    flex: none;
  }
`

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
  }

  th {
    font-weight: 600;
  }
`

export default InvoicingRateCard
